<script>
import CalendarMenuTab from '@/system/components/menu/CalendarMenuTab.vue';
import ClientsMenuTab from '@/system/components/menu/ClientsMenuTab.vue';
import Apps from '@/core/Apps';
import InvoicesMenuTab from '@/system/components/menu/InvoicesMenuTab.vue';
import MessagesMenuItem from '@/system/components/global/messages/global/MessagesMenuItem.vue';
import NotificationMenuItem from '@/system/components/menu/NotificationMenuItem.vue';
import Search from '@/system/components/menu/Search.vue';

export default {
    components: {
        CalendarMenuTab,
        ClientsMenuTab,
        InvoicesMenuTab,
        MessagesMenuItem,
        NotificationMenuItem,
        Search,
    },
    props: {
        menuActive: {
            type: String,
            default: '',
        },
        hasViewedMultipleBookings: {
            required: true,
            type: Boolean
        },
        hasViewedMultipleClients: {
            required: true,
            type: Boolean
        },
        hasViewedMultipleInvoices: {
            required: true,
            type: Boolean
        },
        isTenant: {
            required: true,
            type: Boolean
        },
        showNewAppsAlert: {
            required: true,
            type: Boolean
        },
        isLocal: {
            required: true,
            type: Boolean
        },
        isTreatmentsModuleEnabled: {
            default: false,
            type: Boolean
        },
        initialUnreadMessagesCount: {
            required: true,
            type: Number
        },
        hasUnreadMessages: {
            required: true,
            type: Boolean
        },
        initialNotificationCount: {
            required: true,
            type: Number
        },
        importantNotifications: {
            required: true,
            type: Number
        },
        isClientsOverviewEnabled: {
            default: false,
            type: Boolean
        },
    },
    data() {
        return {
            navSections: [
                {
                    heading: null,
                    id: 'tools-section',
                    items: [
                        {
                            id: 'search',
                            className: 'global-search',
                            icon: 'navigation-search',
                            condition: () => true,
                            isActive: () => false,
                            label: 'global.search',
                            isRelatedOpen: false,
                            relatedComponent: 'search',
                            showRelatedComponent: true,
                            openOnClick: true,
                        },
                        {
                            id: 'notifications',
                            className: 'navigation-notifications',
                            icon: 'navigation-notifications',
                            label: 'global.notification_menu_notifications',
                            condition: () => this.canSeeLogs,
                            isActive: () => false,
                            isRelatedOpen: false,
                            relatedComponent: 'notification-menu-item',
                            showRelatedComponent: true,
                            openOnClick: true,
                        },
                        {
                            id: 'messages',
                            className: 'navigation-messages',
                            icon: 'interface-chat',
                            label: 'client.messages',
                            href: '/app/messages-overview',
                            condition: () => this.canSeeMessages,
                            isActive: () => this.menuActive === 'messages',
                            isRelatedOpen: false,
                            relatedComponent: 'messages-menu-item',
                            showRelatedComponent: this.hasUnreadMessages,
                        },
                    ]
                },

                {
                    heading: 'global.workspace',
                    id: 'workspace-section',
                    items: [
                        {
                            id: 'calendar',
                            className: 'navigation-calendar',
                            icon: 'interface-calendar',
                            label: 'global.calendar',
                            href: '/app/calendar',
                            condition: () => this.canSeeCalendars,
                            isActive: () => this.menuActive === 'calendar',
                            showRelatedComponent: this.hasViewedMultipleBookings,
                            isRelatedOpen: false,
                            relatedComponent: 'calendar-menu-tab',
                        },
                        {
                            id: 'clients',
                            className: 'navigation-clients',
                            icon: 'user-multiple',
                            label: 'global.clients',
                            href: this.isClientsOverviewEnabled ? '/app/clients/overview' : '/app/clients',
                            condition: () => this.canSeeClients,
                            isActive: () => this.menuActive === 'clients',
                            showRelatedComponent: this.hasViewedMultipleClients,
                            isRelatedOpen: false,
                            relatedComponent: 'clients-menu-tab',
                        },
                        {
                            id: 'medcom',
                            className: 'navigation-medcom',
                            icon: 'interface-folder',
                            label: 'apps.medcom.documents',
                            href: '/app/medcom/documents',
                            condition: () => this.isMedcomActive && this.canSeeMedCom,
                            isActive: () => this.menuActive === 'medcom',
                        },
                        {
                            id: 'helfo-settlements',
                            className: 'navigation-helfo-settlements',
                            icon: 'file-multiple',
                            label: 'apps.helfo_settlements.name',
                            href: '/app/helfo/settlements/overview',
                            condition: () => this.isHelfoSettlementsActive && this.canSeeClients,
                            isActive: () => this.menuActive === 'helfo-settlements-overview',
                        },
                        {
                            id: 'products',
                            className: 'navigation-products',
                            icon: 'file-treatment-plan',
                            label: 'global.products',
                            href: '/app/products',
                            condition: () => this.canSeeProducts,
                            isActive: () => this.menuActive === 'products',
                        },
                        {
                            id: 'sales-products',
                            className: 'navigation-sales-products',
                            icon: 'fa-regular fa-scale-unbalanced',
                            label: 'apps.sales.name',
                            href: '/app/sales',
                            condition: () => this.canSeeSalesProducts && this.isSalesActive,
                            isActive: () => this.menuActive === 'sales-products',
                        },
                        {
                            id: 'invoices',
                            className: 'navigation-invoices',
                            icon: 'file-invoice',
                            label: 'global.invoices',
                            href: '/app/invoices',
                            condition: () => this.canSeeInvoices,
                            isActive: () => this.menuActive === 'invoices',
                            showRelatedComponent: this.hasViewedMultipleInvoices,
                            isRelatedOpen: false,
                            relatedComponent: 'invoices-menu-tab',
                        },
                        {
                            id: 'giftcards',
                            className: 'navigation-giftcards',
                            icon: 'fa-regular fa-gift-card',
                            label: 'giftcards.title_plural',
                            href: '/app/giftcards',
                            condition: () => this.canSeeGiftcards && this.isGiftcardsActive,
                            isActive: () => this.menuActive === 'giftcards',
                        },
                        {
                            id: 'online-courses',
                            className: 'navigation-online-courses',
                            icon: 'interface-whiteboard',
                            label: 'apps.online_courses.name',
                            href: '/app/online-courses',
                            condition: () => this.canSeeOnlineCourses && this.isOnlineCoursesActive,
                            isActive: () => this.menuActive === 'onlinecourses',
                        },
                        {
                            id: 'helsenett',
                            className: 'navigation-helsenett',
                            icon: 'interface-email',
                            label: 'apps.helsenett.name',
                            href: '/app/helsenett/edi',
                            condition: () => this.isHelsenettActive && this.canSeeHelsenett,
                            isActive: () => this.menuActive === 'helsenett',
                        },
                        {
                            id: 'statistics',
                            className: 'navigation-statistics',
                            icon: 'graph-line',
                            label: 'statistics.title',
                            href: '/app/statistics',
                            condition: () => this.canSeeStatistics,
                            isActive: () => this.menuActive === 'statistics',
                        },
                        {
                            id: 'statistics',
                            className: 'navigation-statistics',
                            icon: 'graph-line',
                            label: 'statistics.title',
                            href: '/app/statistics/tenancy',
                            condition: () => !this.canSeeStatistics && this.isTenant,
                            isActive: () => this.menuActive === 'statistics',
                        },
                        {
                            id: 'reports',
                            className: 'navigation-reports',
                            icon: 'fa-duotone fa-folders',
                            label: 'reports.title',
                            href: '',
                            condition: () => this.canSeeReports,
                            isActive: () => false,
                            subItems: [
                                {
                                    id: 'asa4313',
                                    label: 'ASA 4313',
                                    href: '/app/reports/asa-4313',
                                    condition: () => this.canSeeReports,
                                    isActive: () => this.menuActive === 'reports',
                                }
                                // TODO: add NPR report when available (https://linear.app/nordhealth/issue/AGI-5699/add-sub-nav-menu)
                                // {
                                //     name: 'NPR',
                                //     type: 'npr',
                                //     icon: 'nord-icon-npr',
                                //     route: '/app/reports/npr',
                                // },
                            ],
                        },
                        {
                            id: 'easypay',
                            className: 'navigation-easy-pay',
                            icon: 'generic-credit-card',
                            label: 'online_payment.online_payment',
                            href: '/app/easypay',
                            condition: () => this.canSeeOnlinePayments && this.isOnlinePaymentActive,
                            isActive: () => this.menuActive === 'easypay',
                        },
                        {
                            id: 'treatments',
                            className: 'navigation-treatments',
                            icon: 'medical-bandage',
                            label: 'apps.treatments.name',
                            href: '/app/treatments',
                            condition: () => this.canSeeTreatments,
                            isActive: () => this.menuActive === 'treatments',
                        },
                        {
                            id: 'settings',
                            className: 'navigation-settings',
                            icon: 'navigation-settings',
                            label: 'global.settings',
                            href: '/app/settings',
                            condition: () => true,
                            isActive: () => this.menuActive === 'settings',
                        },
                        {
                            id: 'apps',
                            className: 'navigation-apps',
                            icon: 'navigation-catalog',
                            label: 'settings.apps',
                            href: '/app/settings/apps',
                            condition: () => this.canEditApps,
                            isActive: () => this.menuActive === 'apps',
                            showAlert: this.showNewAppsAlert,
                        },
                        {
                            id: 'accounts',
                            className: 'navigation-accounts',
                            icon: 'navigation-finances',
                            label: 'Accounting',
                            href: '/app/accounting',
                            condition: () => this.isLocal,
                            isActive: () => this.menuActive === 'accounts',
                        },
                    ]
                },
            ],
            unreadMessagesCount: this.initialUnreadMessagesCount,
            newNotificationCount: this.initialNotificationCount.new_notifications,
            hoveredTabId: null,
            hoverTimeout: null,
        }
    },
    methods: {
        closeRelatedComponent(tab) {
            tab.isRelatedOpen = false;
        },
        formatCountForRedBubble(count) {
            return count > 9 ? '9+' : count;
        },
        setHoverTabId(id) {
            clearTimeout(this.hoverTimeout);

            this.hoverTimeout = setTimeout(() => {
            this.hoveredTabId = id;
            }, 200); // show delay
        },
        clearHoverTabId(id) {
            this.hoverTimeout = setTimeout(() => {
            if (this.hoveredTabId === id) {
                this.hoveredTabId = null;
            }
            }, 200); // hide delay
        },
        cancelClear() {
            clearTimeout(this.hoverTimeout);
        },
        onNavItemClick(tab) {
            if (tab.openOnClick) {
                tab.isRelatedOpen = !tab.isRelatedOpen;
            }
        },
        getRelatedProps(tab) {
            if (tab.id === 'notifications') {
                return {
                    initial_notification_count: this.initialNotificationCount,
                    important_notifications: this.importantNotifications
                };
            }
            return {};
        },
        getNotificationDot(tab) {
            if (tab.id === 'messages' && this.unreadMessagesCount > 0) {
                return { visible: true, text: this.formattedUnreadMessagesCount };
            }

            if (tab.id === 'notifications' && (this.newNotificationCount || this.importantNotifications)) {
                return {
                    visible: true,
                    text: this.importantNotifications
                    ? this.trans('global.important_information')
                    : this.formattedNewNotificationCount,
                };
            }

            if (tab.showAlert) {
                return { visible: true, text: null };
            }

            return { visible: false };
        },
        getHeader(section) {
            transKey = section?.heading;
            return trans(transKey);
        }
    },
    computed: {
        canSeeCalendars() {
            return this.$can('see-calendars');
        },

        canSeeClients() {
            return this.$canEither('see-clients', 'see-clientsassigned');
        },

        isMedcomActive() {
            return this.$store.getters['apps/isAppEnabled'](Apps.Medcom);
        },

        canSeeMedCom() {
            return this.$canEither('see-medcom', 'see-medcomassigned');
        },

        isHelfoSettlementsActive() {
            return this.$store.getters['apps/isAppEnabled'](Apps.HelfoSettlements);
        },

        canSeeProducts() {
            return this.$can('see-products');
        },

        canSeeSalesProducts() {
            return this.$can('see-salesproducts');
        },

        isSalesActive() {
            return this.$store.getters['apps/isAppEnabled'](Apps.Sales);
        },

        canSeeInvoices() {
            return this.$canEither('see-invoices', 'see-invoicesassigned');
        },

        canSeeGiftcards() {
            return this.$can('see-giftcards');
        },

        canSeeReports() {
            // show the Reports based on Helfo Settlements
            return this.isHelfoSettlementsActive && this.canSeeClients
        },

        isGiftcardsActive() {
            return this.$store.getters['apps/isAppEnabled'](Apps.Giftcards);
        },

        canSeeOnlineCourses() {
            return this.$can('see-onlinecourses');
        },

        isOnlineCoursesActive() {
            return this.$store.getters['apps/isAppEnabled'](Apps.OnlineCourses);
        },

        canSeeHelsenett() {
            return this.$can('see-helsenettmessaging');
        },

        isHelsenettActive() {
            return this.$store.getters['apps/isAppEnabled'](Apps.Helsenett);
        },

        canSeeStatistics() {
            return this.$can('see-statistics');
        },

        canSeeOnlinePayments() {
            return this.$canEither('see-onlinepayment', 'see-onlinepaymentassigned');
        },

        canSeeTreatments() {
            return this.isTreatmentsModuleEnabled
                && this.$store.getters['apps/isAppEnabled'](Apps.Treatments)
                && this.$canEither('see-clients', 'see-clientsassigned');
        },

        isOnlinePaymentActive() {
            return this.$store.getters['apps/isAppEnabled'](Apps.EasyPay);
        },

        canEditApps() {
            return this.$can('edit-apps');
        },

        canSeeMessages() {
            return this.$canEither('see-clients', 'see-clientsassigned') && this.$store.getters['apps/isAppEnabled'](Apps.SecureMessages);
        },

        canSeeLogs() {
            return this.$canEither('see-logs', 'see-logsassigned');
        },

        availableNavSections() {
            return this.navSections
                .map(section => ({
                    ...section,
                    items: section.items.filter(item => item.condition?.())
                }))
                .filter(section => section.items.length);
        },

        formattedUnreadMessagesCount() {
            return this.formatCountForRedBubble(this.unreadMessagesCount);
        },

        formattedNewNotificationCount() {
            return this.formatCountForRedBubble(this.newNotificationCount);
        },
    },

    mounted() {
        // Updates red badge on the messages menu item
        this.$event_bus.$on('remove-from-envelope-message-count', (read_messages) => {
            if (this.unreadMessagesCount > 0) {
                this.unreadMessagesCount -= read_messages;
            }
        });

        this.$event_bus.$on('message-marked-as-unread', () => {
            this.unreadMessagesCount++;
        });
    },
}
</script>

<template>
    <template v-for="(section, index) in availableNavSections" :key="section.id">
        <nord-nav-group :heading="getHeader(section)">
            <template v-for="(tab, index) in section.items" :key="tab.id">
                <!-- Wrapper div to ensure hover cards align correctly -->
                <div>
                    <nord-nav-item
                        :class="[tab.className, tab.showRelatedComponent ? 'inline-block w-full' : '']"
                        :active="tab.isActive()"
                        :icon="tab.icon.startsWith('fa') ? '' : tab.icon"
                        :href="tab.href"
                        @click="onNavItemClick(tab)"
                        v-on="(tab.showRelatedComponent && !tab?.openOnClick)  ? {
                            mouseenter: () => setHoverTabId(tab.id),
                            mouseleave: () => clearHoverTabId(tab.id)
                        } : {}"

                    >
                        <!-- Parent navigation item -->
                        <nord-stack
                            direction="horizontal"
                            justify-content="space-between"
                            align-items="center"
                        >
                            <i v-if="tab.icon.startsWith('fa')" :class="tab.icon"></i>
                            <nord-stack direction="horizontal" align-items="center">
                                {{ trans(tab.label) }}

                                <template v-if="getNotificationDot(tab).visible">
                                    <span class="notification-dot">
                                        {{ getNotificationDot(tab).text }}
                                    </span>
                                </template>
                            </nord-stack>

                            <nord-icon v-if="tab.openOnClick && tab.showRelatedComponent" class="nav-item-submenu-icon" name="interface-dropdown-small"/>
                        </nord-stack>

                        <!-- Sub navigation items -->
                        <template v-if="tab.subItems">
                            <nord-nav-group slot="subnav" v-for="(subTab, index) in tab.subItems" :key="subTab.id">
                                <nord-nav-item v-if="subTab?.condition?.()" :href="subTab?.href" :active="subTab?.isActive()">{{ subTab?.label }}</nord-nav-item>
                            </nord-nav-group>
                        </template>
                    </nord-nav-item>

                    <!-- Related components: On click actions -->
                    <template v-if="tab.showRelatedComponent">
                        <Teleport to="body" v-if="tab.id === 'search'">
                            <component
                            :is="tab.relatedComponent"
                            :open="tab.isRelatedOpen"
                            @close="() => closeRelatedComponent(tab)"
                            />
                        </Teleport>

                        <!-- Normal open-on-click component -->
                        <component
                            v-else-if="tab.openOnClick"
                            :is="tab.relatedComponent"
                            :open="tab.isRelatedOpen"
                            v-bind="getRelatedProps(tab)"
                            @close="() => closeRelatedComponent(tab)"
                        />

                        <!-- Hover-triggered popout -->
                        <nord-popout
                            v-else
                            :open="hoveredTabId === tab.id"
                            class="float-right hidden md:inline-block"
                            @mouseenter="cancelClear()"
                            @mouseleave="() => clearHoverTabId(tab.id)"
                        >
                            <component
                            class="max-w-lg overflow-y-auto"
                            style="max-height: 450px;"
                            :is="tab.relatedComponent"
                            :open="hoveredTabId === tab.id"
                            />
                        </nord-popout>
                    </template>
                </div>
            </template>
        </nord-nav-group>
    </template>
</template>

<style lang="scss" scoped>
.notification-dot {
    background-color: var(--n-color-status-notification);
    color: white;
    display: inline-block;
    border-radius: 99999px;
    font-size: 9px;
    font-weight: bold;
    line-height: 1;
    padding: 4px;
}

.nav-item-submenu-icon {
    width: 14px;
}

.link-overlay {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
}
</style>

